export const agencyKeys = {
  practices: (params?: Record<string, unknown>) =>
    params ? (['agency', 'practices', params] as const) : (['agency', 'practices'] as const),
  practice: (id: string) => ['agency', 'practices', id] as const,
  // Every managed practice in one page — shared by the top-bar and workspace practice switchers.
  allPractices: () => ['agency', 'practices', 'all'] as const,
  reviewSmsStatus: (practiceId: string, locationId: string) =>
    ['agency', 'practices', practiceId, 'review-sms-status', locationId] as const,
  texting: (practiceId: string, locationId: string) =>
    ['agency', 'practices', practiceId, 'texting', locationId] as const,
  trackedLinks: (practiceId: string) =>
    ['agency', 'practices', practiceId, 'tracked-links'] as const,
  trackedLinkClicks: (practiceId: string, linkId: string) =>
    ['agency', 'practices', practiceId, 'tracked-links', linkId, 'clicks'] as const,
  stats: () => ['agency', 'stats'] as const,
  pinnedPractices: () => ['agency', 'me', 'pinned-practices'] as const,
  metaTokenStatus: () => ['agency', 'meta-ads', 'token'] as const,
  metaAvailableAccounts: () => ['agency', 'meta-ads', 'available-accounts'] as const,
  metaAvailablePages: (adAccountId?: string) =>
    ['agency', 'meta-ads', 'available-pages', adAccountId ?? 'all'] as const,
  metaLocationStatus: (locationId: string) =>
    ['agency', 'meta-ads', 'locations', locationId, 'status'] as const,
  vapiCalls: (practiceId: string) => ['agency', 'vapi-calls', practiceId] as const,
  auditStatus: (practiceId: string, locationId: string) =>
    ['agency', 'audit-status', practiceId, locationId] as const,
};

/**
 * What is left of the command-center keys: the per-practice panels the practice
 * workspace still renders. The list and summary keys went with the two practice
 * tables the Portfolio view replaced.
 */
export const commandCenterKeys = {
  all: ['commandCenter'] as const,
  practiceReviewFunnel: (practiceId: string, params?: Record<string, unknown>) =>
    ['commandCenter', 'practiceReviewFunnel', practiceId, params] as const,
  practiceTextFunnel: (practiceId: string, params?: Record<string, unknown>) =>
    ['commandCenter', 'practiceTextFunnel', practiceId, params] as const,
  reviewRequests: (range: string) => ['commandCenter', 'reviewRequests', range] as const,
  practiceAgents: (practiceId: string) => ['commandCenter', 'practiceAgents', practiceId] as const,
  // The agent is part of the key: a location holds one mission per agent, so without it every
  // agent's row would share one cache entry and render the first agent's mission.
  agentStatus: (practiceId: string, locationId: string, roleKey: string, all: boolean) =>
    ['commandCenter', 'agentStatus', practiceId, locationId, roleKey, all] as const,
  runLog: (practiceId: string, locationId: string, roleKey: string) =>
    ['commandCenter', 'runLog', practiceId, locationId, roleKey] as const,
  taskTimeline: (practiceId: string, locationId: string, taskId: string) =>
    ['commandCenter', 'taskTimeline', practiceId, locationId, taskId] as const,
  // Prefixes for invalidating every status / timeline of one practice or location.
  agentStatuses: (practiceId: string) => ['commandCenter', 'agentStatus', practiceId] as const,
  taskTimelines: (practiceId: string, locationId: string) =>
    ['commandCenter', 'taskTimeline', practiceId, locationId] as const,
  observations: (practiceId: string, locationId: string, roleKey: string) =>
    ['commandCenter', 'observations', practiceId, locationId, roleKey] as const,
  clientFacts: (practiceId: string, locationId: string) =>
    ['commandCenter', 'clientFacts', practiceId, locationId] as const,
  practiceMission: (practiceId: string, locationId: string, roleKey: string) =>
    ['commandCenter', 'practiceMission', practiceId, locationId, roleKey] as const,
  // Prefix of every practiceMission key for one practice — used to invalidate all of its missions.
  practiceMissions: (practiceId: string) =>
    ['commandCenter', 'practiceMission', practiceId] as const,
  // Per (location, agent): the three task counts on the practice's Agents tab.
  // Per location: the day-one report is written once per location, not per agent.
  dayOneReport: (practiceId: string, locationId: string) =>
    ['commandCenter', 'dayOneReport', practiceId, locationId] as const,
};

export const portfolioKeys = {
  all: ['agencyPortfolio'] as const,
  portfolio: (params?: Record<string, unknown>) => ['agencyPortfolio', 'rows', params] as const,
  movers: () => ['agencyPortfolio', 'movers'] as const,
  work: (params?: Record<string, unknown>) => ['agencyPortfolio', 'work', params] as const,
  workSummary: () => ['agencyPortfolio', 'workSummary'] as const,
  pulse: (practiceId: string) => ['agencyPortfolio', 'pulse', practiceId] as const,
};

/**
 * Ad configuration is read per location, not per practice: Google authorises at
 * the practice level but Meta assigns an ad account per location, and campaigns
 * are location-scoped on both.
 */
export const adConfigKeys = {
  all: ['agencyAdConfig'] as const,
  overview: (practiceId: string, locationId: string) =>
    ['agencyAdConfig', 'overview', practiceId, locationId] as const,
  drafts: (practiceId: string, locationId: string) =>
    ['agencyAdConfig', 'drafts', practiceId, locationId] as const,
  draftOperations: (practiceId: string, locationId: string, draftId: string) =>
    ['agencyAdConfig', 'draftOperations', practiceId, locationId, draftId] as const,
};

/** "Campaigns to check" on the agency ads page: match rows of a shared ad account, per location. */
export const campaignReviewKeys = {
  all: ['agencyCampaignReview'] as const,
  list: (practiceId: string, locationId: string) =>
    ['agencyCampaignReview', 'list', practiceId, locationId] as const,
};

/** "New patients from ads" on the agency ads page: the practice's own counts, per location. */
export const adOutcomeKeys = {
  all: ['agencyAdOutcomes'] as const,
  list: (practiceId: string, locationId: string) =>
    ['agencyAdOutcomes', 'list', practiceId, locationId] as const,
};

export const supervisionKeys = {
  all: ['agencySupervision'] as const,
  cards: () => ['agencySupervision', 'cards'] as const,
};

/** Rollout & Flags: the per-practice × per-agent task switchboard and its change history. */
export const rolloutKeys = {
  all: ['agencyRollout'] as const,
  agentTasks: () => ['agencyRollout', 'agentTasks'] as const,
  history: () => ['agencyRollout', 'history'] as const,
};

/** Skills Registry + Agent Flow Map: agent-suite's on-disk modules, read-only. */
export const registryKeys = {
  all: ['agencyRegistry'] as const,
  registry: () => ['agencyRegistry', 'registry'] as const,
  skill: (key: string) => ['agencyRegistry', 'skill', key] as const,
  files: (key: string) => ['agencyRegistry', 'files', key] as const,
  writeBack: () => ['agencyRegistry', 'writeBack'] as const,
  changes: (params?: { owner?: string; dirName?: string }) =>
    ['agencyRegistry', 'changes', params ?? {}] as const,
};

/** Communication preferences per practice (one record per agent). */
export const commsKeys = {
  all: ['agencyComms'] as const,
  practice: (practiceId: string) => ['agencyComms', practiceId] as const,
};

/** Per-practice skill overrides (agent-suite rows keyed by location). */
export const overrideKeys = {
  all: ['agencySkillOverrides'] as const,
  list: () => ['agencySkillOverrides', 'all'] as const,
  location: (practiceId: string, locationId: string) =>
    ['agencySkillOverrides', 'location', practiceId, locationId] as const,
  files: (practiceId: string, locationId: string, id: string) =>
    ['agencySkillOverrides', 'files', practiceId, locationId, id] as const,
};

/** Command Center > Modules > Community (Joe). */
export const communityAdminKeys = {
  settings: (practiceId: string, locationId: string) =>
    ['agency', 'community', 'settings', practiceId, locationId] as const,
  runs: (practiceId: string, locationId: string) =>
    ['agency', 'community', 'runs', practiceId, locationId] as const,
  eventSources: (practiceId: string, locationId: string) =>
    ['agency', 'community', 'event-sources', practiceId, locationId] as const,
};
